import { TypedEventEmitter } from './event-emitter.js';
import { DEFAULT_ROLL_CONFIG } from './constants.js';
import { OddballCC } from './types.js';
import type {
  RollDetectorConfig,
  RollDetectorEventMap,
  RollStartEvent,
  RollingEvent,
  RollDirectionChangeEvent,
  RollEndEvent,
  RollDiagnostics,
} from './types.js';

type Heading = 'x' | 'y' | 'z';

interface OrientationSnapshot {
  x: number;
  y: number;
  z: number;
}

export class RollDetector extends TypedEventEmitter<RollDetectorEventMap> {
  private config: RollDetectorConfig;

  // CC state (fed externally)
  private spinSmoothed = 0;
  private orientation: OrientationSnapshot = { x: 64, y: 64, z: 64 };

  // Roll state
  private _rolling = false;
  private rollStartEmitted = false;
  private rollStartTime = 0;
  private rollStartOrientation: OrientationSnapshot = { x: 64, y: 64, z: 64 };
  private rollLastUpdateTime = 0;
  private rollSpinSamples: number[] = [];
  private rollDirectionChanges = 0;
  private rollCurrentHeading: Heading = 'x';
  private rollTapDuringRoll = false;

  // Orientation delta tracking for direction detection
  private prevOrientation: OrientationSnapshot = { x: 64, y: 64, z: 64 };
  private deltaHistory: { x: number; y: number; z: number }[] = [];
  private prevSmoothedDelta: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };

  // Angular velocity roll detection
  private gravityBuffer: { gx: number; gy: number; gz: number; t: number }[] = [];
  private orientAxesSeen = 0; // bitmask: 1=x, 2=y, 4=z

  // Dual-path energy
  private omegaFast = 0;
  private omegaSlowRaw = 0;
  private omegaFastSmoothed = 0;
  private omegaSlowSmoothed = 0;
  private spinEma = 0;
  private energyFast = 0;
  private energySlow = 0;

  // Roll start hold timers
  private rollHoldTimer = 0;
  private rollHoldAngle = 0;
  private rollFastHoldTimer = 0;
  private rollFastHoldAngle = 0;

  // Roll end
  private rollDropTimer = 0;
  private rollBackoffUntil = 0;

  // Impact grace
  private rollImpactGraceUntil = 0;
  private prevOmegaSlow = 0;

  // Low-frequency end condition
  private rollLowFreqCount = 0;

  private lastOrientTime = 0;
  private currentOscillationFreq = 0;
  private oscillationFreqSamples: number[] = [];

  get rolling(): boolean {
    return this._rolling;
  }

  constructor(config?: Partial<RollDetectorConfig>) {
    super();
    this.config = { ...DEFAULT_ROLL_CONFIG, ...config };
  }

  getDiagnostics(now?: number): RollDiagnostics {
    const t = now ?? this.lastOrientTime;
    return {
      rolling: this._rolling,
      backoff: t < this.rollBackoffUntil,
      energyFast: this.energyFast,
      energySlow: this.energySlow,
      omegaFast: this.omegaFastSmoothed,
      omegaSlow: this.omegaSlowSmoothed,
      spinEma: this.spinEma,
      oscillationFreq: this.currentOscillationFreq,
      slowHoldMs: this.rollHoldTimer,
      slowHoldAngle: this.rollHoldAngle,
      fastHoldMs: this.rollFastHoldTimer,
      dropTimerMs: this.rollDropTimer,
      lowFreqCount: this.rollLowFreqCount,
      impactGraceActive: t < this.rollImpactGraceUntil,
      impactGraceRemainMs: Math.max(0, this.rollImpactGraceUntil - t),
      thresholds: {
        fastStartEnergy: this.config.rollFastStartThreshold,
        fastStartOmega: this.config.rollFastMinOmega,
        fastStartHoldMs: this.config.rollFastStartHoldMs,
        slowStartEnergy: this.config.rollSlowStartThreshold,
        slowStartHoldMs: this.config.rollSlowStartHoldMs,
        slowMinAngle: this.config.rollMinStartAngle,
        minOscFreq: this.config.rollMinOscillationFreq,
        spinHighConfidence: this.config.rollSpinHighConfidence,
        endEnergy: this.config.rollEndEnergyThreshold,
        endOmega: this.config.rollEndMinOmega,
        endSpin: this.config.rollEndMinSpin,
        endHoldMs: this.config.rollEndHoldMs,
      },
    };
  }

  /** Process a CC update relevant to roll detection */
  handleCC(cc: number, value: number, timestamp: number): void {
    switch (cc) {
      case OddballCC.SpinSmoothed:
        this.spinSmoothed = value;
        break;

      case OddballCC.OrientX:
      case OddballCC.OrientY:
      case OddballCC.OrientZ: {
        const axis = cc === OddballCC.OrientX ? 'x' : cc === OddballCC.OrientY ? 'y' : 'z';
        this.orientation[axis] = value;

        const bit = cc === OddballCC.OrientX ? 1 : cc === OddballCC.OrientY ? 2 : 4;
        this.orientAxesSeen |= bit;

        if (this.orientAxesSeen !== 7) break;

        const gx = this.orientation.x - 64;
        const gy = this.orientation.y - 64;
        const gz = this.orientation.z - 64;
        const mag = Math.sqrt(gx * gx + gy * gy + gz * gz);

        if (mag < 1) {
          this.lastOrientTime = timestamp;
          break;
        }

        // Gravity gate
        if ((!this._rolling && mag < this.config.rollMinGravityMagnitude) ||
            mag > this.config.rollMaxGravityMagnitude) {
          if (this._rolling) {
            const dt = this.lastOrientTime > 0 ? timestamp - this.lastOrientTime : 0;
            this.rollDropTimer += dt;
            if (this.rollDropTimer >= this.config.rollEndHoldMs) {
              this.endRoll(timestamp - this.config.rollEndHoldMs);
            }
          }
          this.lastOrientTime = timestamp;
          break;
        }

        const inv = 1 / mag;
        this.gravityBuffer.push({ gx: gx * inv, gy: gy * inv, gz: gz * inv, t: timestamp });
        if (this.gravityBuffer.length > this.config.rollGravityBufferSize) {
          this.gravityBuffer.shift();
        }

        this.omegaFast = this.computeAngularVelocity(timestamp, this.config.rollFastWindowMs);
        this.omegaSlowRaw = this.computeAngularVelocity(timestamp, this.config.rollSlowWindowMs);

        this.prevOmegaSlow = this.omegaSlowSmoothed;

        const dt = timestamp - this.lastOrientTime;
        if (dt > 0 && this.lastOrientTime > 0) {
          const alphaFast = 1 - Math.exp(-dt / this.config.rollFastSmoothingTauMs);
          this.omegaFastSmoothed = alphaFast * this.omegaFast + (1 - alphaFast) * this.omegaFastSmoothed;

          const alphaSlow = 1 - Math.exp(-dt / this.config.rollSlowSmoothingTauMs);
          this.omegaSlowSmoothed = alphaSlow * this.omegaSlowRaw + (1 - alphaSlow) * this.omegaSlowSmoothed;

          const alphaSpin = 1 - Math.exp(-dt / this.config.rollSpinSmoothingTauMs);
          this.spinEma = alphaSpin * this.spinSmoothed + (1 - alphaSpin) * this.spinEma;
        } else {
          this.omegaFastSmoothed = this.omegaFast;
          this.omegaSlowSmoothed = this.omegaSlowRaw;
          this.spinEma = this.spinSmoothed;
        }

        this.energyFast = this.omegaFastSmoothed + this.config.rollFastSpinWeight * (this.spinEma / 127);
        this.energySlow = this.omegaSlowSmoothed + this.config.rollSlowSpinWeight * (this.spinEma / 127);

        this.lastOrientTime = timestamp;
        this.currentOscillationFreq = this.omegaSlowSmoothed / (2 * Math.PI);

        // State machine: start roll (three paths)
        if (!this._rolling && timestamp >= this.rollBackoffUntil) {
          const stepDt = dt > 0 ? dt : 0;
          let started = false;

          // Path 1: Fast energy
          if (this.energyFast > this.config.rollFastStartThreshold &&
              this.omegaFastSmoothed > this.config.rollFastMinOmega) {
            this.rollFastHoldTimer += stepDt;
            this.rollFastHoldAngle += this.omegaFastSmoothed * stepDt / 1000;
            if (this.rollFastHoldTimer >= this.config.rollFastStartHoldMs) {
              this.startRoll(timestamp - this.config.rollFastStartHoldMs);
              started = true;
            }
          } else {
            this.rollFastHoldTimer = 0;
            this.rollFastHoldAngle = 0;
          }

          // Path 2: Slow energy
          if (!started &&
              this.energySlow > this.config.rollSlowStartThreshold &&
              this.currentOscillationFreq >= this.config.rollMinOscillationFreq) {
            this.rollHoldTimer += stepDt;
            this.rollHoldAngle += this.omegaSlowSmoothed * stepDt / 1000;
            if (this.rollHoldTimer >= this.config.rollSlowStartHoldMs &&
                this.rollHoldAngle >= this.config.rollMinStartAngle) {
              this.startRoll(timestamp - this.config.rollSlowStartHoldMs);
              started = true;
            }
          } else if (!started) {
            this.rollHoldTimer = 0;
            this.rollHoldAngle = 0;
          }

          // Path 3: Spin-assisted immediate
          if (!started &&
              this.spinEma > this.config.rollSpinHighConfidence &&
              this.omegaFastSmoothed > this.config.rollFastMinOmega) {
            this.startRoll(timestamp);
          }
        }

        // State machine: end roll
        if (this._rolling) {
          const omegaDelta = this.omegaSlowSmoothed - this.prevOmegaSlow;
          if (omegaDelta > this.config.rollImpactOmegaThreshold &&
              this.prevOmegaSlow < this.config.rollImpactOmegaThreshold) {
            this.rollImpactGraceUntil = timestamp + this.config.rollImpactGraceMs;
          }

          if (timestamp < this.rollImpactGraceUntil) {
            this.rollDropTimer = 0;
          } else if (this.energySlow < this.config.rollEndEnergyThreshold &&
                     this.energyFast < this.config.rollEndEnergyThreshold &&
                     this.omegaSlowSmoothed < this.config.rollEndMinOmega &&
                     this.omegaFastSmoothed < this.config.rollEndMinOmega &&
                     this.spinEma < this.config.rollEndMinSpin) {
            this.rollDropTimer += dt > 0 ? dt : 0;
            if (this.rollDropTimer >= this.config.rollEndHoldMs) {
              this.endRoll(timestamp - this.config.rollEndHoldMs);
            }
          } else {
            this.rollDropTimer = 0;
          }
          if (this._rolling) {
            this.updateOrientationDelta(timestamp);
            this.maybeEmitRollingUpdate(timestamp);
          }
        }
        break;
      }
    }
  }

  /** Notify roll detector of a tap (sets rollTapDuringRoll flag) */
  handleTap(timestamp: number): void {
    if (this._rolling) {
      this.rollTapDuringRoll = true;
    }
  }

  /** Force-end an active roll (called by coordinator when throw starts) */
  forceEnd(timestamp: number): void {
    if (this._rolling) {
      this.endRoll(timestamp);
    }
  }

  // ── Angular velocity computation ────────────────────────────────────

  private computeAngularVelocity(timestamp: number, windowMs: number): number {
    const buf = this.gravityBuffer;
    if (buf.length < 2) return 0;

    const target = timestamp - windowMs;
    let startIdx = 0;
    for (let i = buf.length - 2; i >= 0; i--) {
      if (buf[i].t <= target) { startIdx = i; break; }
      startIdx = i;
    }

    let totalAngle = 0;
    for (let i = startIdx + 1; i < buf.length; i++) {
      const prev = buf[i - 1];
      const curr = buf[i];
      const dot = curr.gx * prev.gx + curr.gy * prev.gy + curr.gz * prev.gz;
      totalAngle += Math.acos(Math.max(-1, Math.min(1, dot)));
    }

    const dtSec = (buf[buf.length - 1].t - buf[startIdx].t) / 1000;
    if (dtSec <= 0) return 0;

    return totalAngle / dtSec;
  }

  // ── Roll lifecycle ──────────────────────────────────────────────────

  private startRoll(timestamp: number): void {
    this._rolling = true;
    this.rollStartEmitted = false;
    this.rollStartTime = timestamp;
    this.rollStartOrientation = { ...this.orientation };
    this.rollLastUpdateTime = timestamp;
    this.rollSpinSamples = [];
    this.rollDirectionChanges = 0;
    this.rollCurrentHeading = this.getDominantAxis();
    this.rollTapDuringRoll = false;
    this.deltaHistory = [];
    this.prevOrientation = { ...this.orientation };
    this.prevSmoothedDelta = { x: 0, y: 0, z: 0 };
    this.oscillationFreqSamples = [];
    this.rollDropTimer = 0;
    this.rollFastHoldTimer = 0;
    this.rollFastHoldAngle = 0;
    this.rollImpactGraceUntil = 0;
    this.rollLowFreqCount = 0;
  }

  private confirmRollStart(): void {
    if (this.rollStartEmitted) return;
    this.rollStartEmitted = true;
    const event: RollStartEvent = { type: 'rollStart', timestamp: this.rollStartTime };
    this.emit('rollStart', event);
  }

  private maybeEmitRollingUpdate(timestamp: number): void {
    if (timestamp - this.rollLastUpdateTime < this.config.rollingUpdateIntervalMs) return;

    this.confirmRollStart();
    this.rollLastUpdateTime = timestamp;
    this.rollSpinSamples.push(this.spinSmoothed);
    this.oscillationFreqSamples.push(this.currentOscillationFreq);

    const event: RollingEvent = {
      type: 'rolling',
      startTime: this.rollStartTime,
      elapsed: timestamp - this.rollStartTime,
      spinSpeed: this.spinSmoothed,
      orientation: { ...this.orientation },
      heading: this.rollCurrentHeading,
      oscillationFreq: this.currentOscillationFreq,
    };
    this.emit('rolling', event);

    // End roll if oscillation frequency stays too low
    const freqFast = this.omegaFastSmoothed / (2 * Math.PI);
    if (this.currentOscillationFreq < this.config.rollMinOscillationFreq &&
        freqFast < this.config.rollMinOscillationFreq) {
      this.rollLowFreqCount++;
      if (this.rollLowFreqCount >= this.config.rollEndMaxLowFreqUpdates) {
        this.endRoll(timestamp);
      }
    } else {
      this.rollLowFreqCount = 0;
    }
  }

  private endRoll(timestamp: number): void {
    this._rolling = false;
    this.rollBackoffUntil = timestamp + this.config.rollBackoffMs;
    this.rollHoldTimer = 0;
    this.rollHoldAngle = 0;

    if (!this.rollStartEmitted) return;

    const duration = timestamp - this.rollStartTime;
    const avgSpin =
      this.rollSpinSamples.length > 0
        ? this.rollSpinSamples.reduce((a, b) => a + b, 0) / this.rollSpinSamples.length
        : 0;
    const avgFreq =
      this.oscillationFreqSamples.length > 0
        ? this.oscillationFreqSamples.reduce((a, b) => a + b, 0) / this.oscillationFreqSamples.length
        : 0;

    const event: RollEndEvent = {
      type: 'rollEnd',
      startTime: this.rollStartTime,
      duration,
      averageSpinSpeed: avgSpin,
      orientationDelta: {
        x: this.orientation.x - this.rollStartOrientation.x,
        y: this.orientation.y - this.rollStartOrientation.y,
        z: this.orientation.z - this.rollStartOrientation.z,
      },
      directionChanges: this.rollDirectionChanges,
      averageOscillationFreq: avgFreq,
    };
    this.emit('rollEnd', event);
  }

  private updateOrientationDelta(timestamp: number): void {
    const delta = {
      x: this.orientation.x - this.prevOrientation.x,
      y: this.orientation.y - this.prevOrientation.y,
      z: this.orientation.z - this.prevOrientation.z,
    };
    this.prevOrientation = { ...this.orientation };
    this.deltaHistory.push(delta);

    const window = this.config.directionChangeSmoothingWindow;
    if (this.deltaHistory.length > window) {
      this.deltaHistory = this.deltaHistory.slice(-window);
    }

    const smoothed = { x: 0, y: 0, z: 0 };
    for (const d of this.deltaHistory) {
      smoothed.x += d.x;
      smoothed.y += d.y;
      smoothed.z += d.z;
    }
    const n = this.deltaHistory.length;
    smoothed.x /= n;
    smoothed.y /= n;
    smoothed.z /= n;

    const newHeading = this.getDominantDeltaAxis(smoothed);
    const prevSign = Math.sign(this.prevSmoothedDelta[this.rollCurrentHeading]);
    const newSign = Math.sign(smoothed[this.rollCurrentHeading]);

    if (
      prevSign !== 0 &&
      newSign !== 0 &&
      prevSign !== newSign &&
      this.deltaHistory.length >= window
    ) {
      const previousHeading = this.rollCurrentHeading;
      this.rollCurrentHeading = newHeading;
      this.rollDirectionChanges++;

      const event: RollDirectionChangeEvent = {
        type: 'rollDirectionChange',
        timestamp,
        previousHeading,
        newHeading,
        elapsed: timestamp - this.rollStartTime,
      };
      this.emit('rollDirectionChange', event);
    } else if (newHeading !== this.rollCurrentHeading) {
      this.rollCurrentHeading = newHeading;
    }

    this.prevSmoothedDelta = smoothed;
  }

  private getDominantAxis(): Heading {
    return 'x';
  }

  private getDominantDeltaAxis(delta: { x: number; y: number; z: number }): Heading {
    const ax = Math.abs(delta.x);
    const ay = Math.abs(delta.y);
    const az = Math.abs(delta.z);
    if (ax >= ay && ax >= az) return 'x';
    if (ay >= az) return 'y';
    return 'z';
  }
}
